import { useCallback, useEffect, useState } from 'react';
import { formatOperationActor } from './AutoDeployOptions';
import { ReasonText } from './InstanceEventsRow';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { getOperationResults } from '../../../services/stacksetsApi';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK, FONT_MONO, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx,
  bodyCellSx, insetPanelSx, emptyTextSx,
} from '../portalStyles';

const RESULTS_POLL_MS = 5000;

export const operationStatusColor = (status) => (
  status === 'succeeded' ? 'success'
    : status === 'running' ? 'info'
      : status === 'partial' ? 'warning'
        : 'error'
);

// AWS StackSetOperationResultStatus → chip color.
const resultColor = (status) => ({
  SUCCEEDED: 'success',
  FAILED: 'error',
  CANCELLED: 'warning',
  RUNNING: 'info',
  PENDING: 'info',
}[status] || 'default');

// OperationResultsRow renders one Operations-tab row. Expanding it shows the
// operation's persisted per-cell results (account, region, status, reason),
// re-polled while the operation is still running.
export default function OperationResultsRow({ operation: o, stackSetId, index = 0, onStop }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await getOperationResults(stackSetId, o.id);
      setResults(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [stackSetId, o.id]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && results === null) load(true);
  };

  useEffect(() => {
    if (!open || o.status !== 'running') return undefined;
    const t = setInterval(() => load(false), RESULTS_POLL_MS);
    return () => clearInterval(t);
  }, [open, o.status, load]);

  return (
    <>
      <TableRow hover sx={rowSx(index)}>
        <TableCell sx={{ ...bodyCellSx, width: 40 }}>
          <IconButton size="small" onClick={toggle} aria-label="operation results">
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={bodyCellSx}>{formatStatusLabel(o.type)}</TableCell>
        <TableCell sx={bodyCellSx}><Chip size="small" label={formatStatusLabel(o.status)} sx={statusChipSx(intentFromColor(operationStatusColor(o.status)))} /></TableCell>
        <TableCell sx={{ ...bodyCellSx, wordBreak: 'break-word' }}>{formatOperationActor(o)}</TableCell>
        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{new Date(o.startedAt).toLocaleString()}</TableCell>
        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>{o.completedAt ? new Date(o.completedAt).toLocaleString() : '—'}</TableCell>
        <TableCell sx={{ ...bodyCellSx, textAlign: 'right' }} align="right">
          {o.status === 'running' && o.awsOperationId && (
            <Button size="small" color="warning" onClick={() => onStop && onStop(o)}>Stop</Button>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: open ? `1px solid ${CK.border}` : 'none' }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ ...insetPanelSx, p: 2, my: 1.5 }}>
              {o.errorMessage && (
                <Alert severity="warning" sx={{ mb: 1, borderRadius: '8px' }}>{o.errorMessage}</Alert>
              )}
              {error && <Alert severity="error" sx={{ mb: 1, borderRadius: '8px' }}>{error}</Alert>}
              {loading && <CircularProgress size={16} thickness={5} sx={{ color: CK.blue }} />}
              {results && results.length === 0 && !loading && (
                <Typography sx={{ ...emptyTextSx, fontSize: '0.875rem' }}>
                  No per-instance results recorded for this operation.
                </Typography>
              )}
              {results && results.length > 0 && (
                <TableContainer sx={{ maxHeight: 360 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={tableHeadRowSx}>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Account</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Region</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Status</TableCell>
                        <TableCell sx={{ ...headCellSx, backgroundColor: CK.pageBg }}>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {results.map((r, i) => (
                        <TableRow key={r.id || `${r.accountId}-${r.region}`} hover sx={rowSx(i)}>
                          <TableCell sx={{ ...bodyCellSx, fontFamily: FONT_MONO, fontSize: '0.8rem' }}>{r.accountId}</TableCell>
                          <TableCell sx={bodyCellSx}>{r.region}</TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip size="small" label={formatStatusLabel(r.status)} sx={statusChipSx(intentFromColor(resultColor(r.status)))} />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, maxWidth: 420, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            <ReasonText reason={r.statusReason} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
